import { currencyFormatter } from '@kidslabs/module-core';
import { calculateOriginalTotalFromItems, cartStoreAPI, useHybridCartPrices, useKicoin } from '@kidslabs/module-quote';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { checkoutStore$ } from '../stores/checkout.store';
import { use$, useSelector } from '@legendapp/state/react';
import { DEFAULT_SHIPPING_FEE } from './use-fast-cart-total';

export const useOrderItems = () => {
    const { t } = useTranslation('checkout');
    const { prices, selectedCartItems } = useHybridCartPrices();

    const { appliedKicoins } = useKicoin();

    // Read discount from fastCartTotals store (updated by useCheckoutTotalsManager
    // which watches discountVersion). Reactive — UI updates automatically when
    // voucher is applied/removed without any independent fetch.
    const fastCartTotals = useSelector(() => cartStoreAPI.state.getCartTotals()?.get());

    const voucherDecrease = useMemo(() => {
        // Priority: store fastCartTotals (server-authoritative, reactive to discountVersion)
        // Fallback: local prices from useHybridCartPrices (before server data arrives)
        const storeDiscount = fastCartTotals?.discount_amount;
        if (storeDiscount != null && storeDiscount !== 0) {
            return -Math.abs(storeDiscount);
        }
        return -(prices?.discounts?.[0]?.amount?.value ?? 0);
    }, [fastCartTotals?.discount_amount, prices]);

    const selectedShippingMethod = use$(checkoutStore$.selectedShippingMethod);

    const shippingAmount = useMemo(() => {
        return selectedShippingMethod?.amount?.value ?? 0;
    }, [selectedShippingMethod?.amount?.value]);

    const shippingAmountDecrease = useMemo(() => {
        return shippingAmount ? 0 : -DEFAULT_SHIPPING_FEE;
    }, [shippingAmount]);

    const discountLabel = useMemo(() => {
        return prices?.discounts?.[0]?.label ?? `${t('text_coupon_title', 'Mã giảm giá')}`;
    }, [prices, t]);

    const totalProducts = useMemo(() => {
        return currencyFormatter(prices?.subtotal_excluding_tax?.value ?? 0);
    }, [prices]);

    const total = useMemo(() => {
        return currencyFormatter(prices?.grand_total?.value ?? 0);
    }, [prices]);
    const discounts = useMemo(() => {
        const discountData = [];

        if (appliedKicoins) {
            discountData.push({
                title: t('text_kicoin_discount_label', 'Giảm giá Kicoin'),
                value: currencyFormatter(appliedKicoins),
            });
        }

        if (voucherDecrease) {
            discountData.push({
                title: t('text_coupon_discount_label', 'Giảm giá Voucher'),
                value: currencyFormatter(voucherDecrease),
            });
        }

        if (shippingAmount === 0) {
            discountData.push({
                title: t('text_shipping_discount_label', 'Giảm giá vận chuyển'),
                value: '-' + currencyFormatter(DEFAULT_SHIPPING_FEE),
            });
        }

        if (!discountData.length) return [];
        const total = (appliedKicoins ?? 0) + (voucherDecrease ?? 0) + shippingAmountDecrease;

        return [
            { title: t('text_total_discount_label', 'Giảm giá'), value: currencyFormatter(total) },
            ...discountData,
        ];
    }, [appliedKicoins, shippingAmount, shippingAmountDecrease, t, voucherDecrease]);

    const shippingFee = useMemo(() => {
        return currencyFormatter(selectedShippingMethod?.amount?.value ?? 0);
    }, [selectedShippingMethod]);

    const stockAvailabilityMap = useSelector(() => cartStoreAPI.state.getAvailableItemsObservable().get());

    const originalTotal = useMemo(() => {
        return calculateOriginalTotalFromItems(selectedCartItems, stockAvailabilityMap);
    }, [selectedCartItems, stockAvailabilityMap]);

    const grandTotal = useMemo(() => {
        return prices?.grand_total?.value ?? 0;
    }, [prices?.grand_total?.value]);

    return {
        voucherDecrease,
        total,
        totalProducts,
        discountLabel,
        discounts,
        appliedKicoins,
        shippingFee,
        originalTotal,
        grandTotal,
    };
};
